
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Team {
  id: string;
  name: string;
  category: string;
  photo?: string;
  logo?: string;
  is_own_team: boolean;
}

interface Player {
  id: string;
  name: string;
  number: number;
  position: string;
  photo?: string;
  team_id: string;
}

interface Staff {
  id: string;
  name: string;
  role: string;
  photo?: string;
  team_id: string;
}

export default function TeamsManager() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'teams' | 'players' | 'staff'>('teams');
  
  // Formularios
  const [teamForm, setTeamForm] = useState({
    name: '',
    category: '',
    photo: '',
    logo: '',
    is_own_team: false
  });
  
  const [playerForm, setPlayerForm] = useState({
    name: '',
    number: 0,
    position: '',
    photo: '',
    team_id: ''
  });
  
  const [staffForm, setStaffForm] = useState({
    name: '',
    role: '',
    photo: '',
    team_id: ''
  });

  const [showTeamForm, setShowTeamForm] = useState(false);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [editingPlayer, setEditingPlayer] = useState<string | null>(null);
  const [editingStaff, setEditingStaff] = useState<string | null>(null);

  // Estados para el editor de imágenes
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [imageEditorData, setImageEditorData] = useState({
    originalImage: '',
    croppedImage: '',
    type: '' as 'team' | 'player' | 'staff' | 'logo' | 'imagen_equipo'
  });

  useEffect(() => {
    fetchTeams();
    fetchPlayers();
    fetchStaff();
  }, []);

  const handleTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const teamData = {
        name: teamForm.name.trim(),
        category: teamForm.category.trim(),
        photo: teamForm.photo || null,
        logo: teamForm.logo || null,
        is_own_team: teamForm.is_own_team
      };

      let result;
      if (editingTeam) {
        result = await Promise.race([
          supabase
            .from('teams')
            .update(teamData)
            .eq('id', editingTeam.id),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
          )
        ]);
      } else {
        result = await Promise.race([
          supabase
            .from('teams')
            .insert([teamData]),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
          )
        ]);
      }
      
      if (result.error) throw result.error;
      
      setTeamForm({ name: '', category: '', photo: '', logo: '', is_own_team: false });
      setShowTeamForm(false);
      setEditingTeam(null);
      
      // Actualizar lista de equipos con timeout
      await Promise.race([
        fetchTeams(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout al actualizar lista')), 5000)
        )
      ]);
      
      // Notificar actualización
      window.dispatchEvent(new CustomEvent('teamDataUpdated'));
    } catch (error) {
      console.error('Error al guardar equipo:', error);
      if (error.message?.includes('Timeout') || error.message?.includes('timeout')) {
        alert('La operación tardó demasiado. Por favor, verifica tu conexión e inténtalo de nuevo.');
      } else {
        alert(`Error al guardar el equipo: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      }
    }
  };

  const handlePlayerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const playerData = {
        name: playerForm.name.trim(),
        number: playerForm.number,
        position: playerForm.position.trim(),
        photo: playerForm.photo ? playerForm.photo.trim() : null,
        team_id: playerForm.team_id
      };

      let result;
      if (editingPlayer) {
        result = await Promise.race([
          supabase
            .from('players')
            .update(playerData)
            .eq('id', editingPlayer),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
          )
        ]);
      } else {
        result = await Promise.race([
          supabase
            .from('players')
            .insert([playerData]),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
          )
        ]);
      }
      
      if (result.error) throw result.error;
      
      setPlayerForm({ name: '', number: 0, position: '', photo: '', team_id: '' });
      setShowPlayerForm(false);
      setEditingPlayer(null);
      
      // Actualizar lista con timeout
      await Promise.race([
        fetchPlayers(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout al actualizar lista')), 5000)
        )
      ]);
      
      // Notificar actualización
      window.dispatchEvent(new CustomEvent('teamDataUpdated'));
    } catch (error) {
      console.error('Error al guardar jugador:', error);
      if (error.message?.includes('Timeout') || error.message?.includes('timeout')) {
        alert('La operación tardó demasiado. Por favor, verifica tu conexión e inténtalo de nuevo.');
      } else {
        alert(`Error al guardar el jugador: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      }
    }
  };

  const handleStaffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const staffData = {
        name: staffForm.name.trim(),
        role: staffForm.role.trim(),
        photo: staffForm.photo ? staffForm.photo.trim() : null,
        team_id: staffForm.team_id
      };

      let result;
      if (editingStaff) {
        result = await Promise.race([
          supabase
            .from('staff')
            .update(staffData)
            .eq('id', editingStaff),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
          )
        ]);
      } else {
        result = await Promise.race([
          supabase
            .from('staff')
            .insert([staffData]),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
          )
        ]);
      }
      
      if (result.error) throw result.error;
      
      setStaffForm({ name: '', role: '', photo: '', team_id: '' });
      setShowStaffForm(false);
      setEditingStaff(null);
      
      // Actualizar lista con timeout
      await Promise.race([
        fetchStaff(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout al actualizar lista')), 5000)
        )
      ]);
      
      // Notificar actualización
      window.dispatchEvent(new CustomEvent('teamDataUpdated'));
    } catch (error) {
      console.error('Error al guardar staff:', error);
      if (error.message?.includes('Timeout') || error.message?.includes('timeout')) {
        alert('La operación tardó demasiado. Por favor, verifica tu conexión e inténtalo de nuevo.');
      } else {
        alert(`Error al guardar el miembro del staff: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      }
    }
  };

  const openMediaSelector = (type: 'team' | 'player' | 'staff' | 'logo' | 'imagen_equipo') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('El archivo es demasiado grande. Máximo 5MB.');
          return;
        }

        if (!file.type.startsWith('image/')) {
          alert('Por favor selecciona un archivo de imagen válido.');
          return;
        }

        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            setImageEditorData({
              originalImage: result,
              croppedImage: result,
              type: type
            });
            setShowImageEditor(true);
          };
          reader.onerror = () => {
            alert('Error al leer el archivo. Por favor intenta de nuevo.');
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error al procesar imagen:', error);
          alert('Error al procesar la imagen');
        }
      }
    };
    input.click();
  };

  const handleImageSave = () => {
    const { croppedImage, type } = imageEditorData;
    
    if (type === 'team') {
      setTeamForm({ ...teamForm, photo: croppedImage });
    } else if (type === 'logo') {
      setTeamForm({ ...teamForm, logo: croppedImage });
    } else if (type === 'player') {
      setPlayerForm({ ...playerForm, photo: croppedImage });
    } else if (type === 'staff') {
      setStaffForm({ ...staffForm, photo: croppedImage });
    }
    
    setShowImageEditor(false);
    setImageEditorData({ originalImage: '', croppedImage: '', type: '' as any });
  };

  const editTeam = (team: Team) => {
    setTeamForm({
      name: team.name,
      category: team.category,
      photo: team.photo || '',
      logo: team.logo || '',
      is_own_team: team.is_own_team
    });
    setEditingTeam(team);
    setShowTeamForm(true);
  };

  const editPlayer = (player: Player) => {
    setPlayerForm(player);
    setEditingPlayer(player.id);
    setShowPlayerForm(true);
  };

  const editStaff = (staffMember: Staff) => {
    setStaffForm(staffMember);
    setEditingStaff(staffMember.id);
    setShowStaffForm(true);
  };

  const deleteTeam = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar este equipo?')) {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error al eliminar equipo:', error);
      } else {
        fetchTeams();
      }
    }
  };

  const deletePlayer = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar este jugador?')) {
      const { error } = await supabase
        .from('players')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error al eliminar jugador:', error);
      } else {
        fetchPlayers();
      }
    }
  };

  const deleteStaffMember = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar este miembro del staff?')) {
      const { error } = await supabase
        .from('staff')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error al eliminar staff:', error);
      } else {
        fetchStaff();
      }
    }
  };

  const filteredPlayers = selectedTeam 
    ? players.filter(p => p.team_id === selectedTeam)
    : players;

  const filteredStaff = selectedTeam 
    ? staff.filter(s => s.team_id === selectedTeam)
    : staff;

  const fetchTeams = async () => {
    try {
      const result = await Promise.race([
        supabase
          .from('teams')
          .select('*')
          .order('name'),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout al cargar equipos')), 8000)
        )
      ]);
      
      if (result.error) {
        console.error('Error al cargar equipos:', result.error);
      } else {
        setTeams(result.data || []);
      }
    } catch (error) {
      console.error('Error al cargar equipos:', error);
      if (error.message?.includes('Timeout') || error.message?.includes('timeout')) {
        alert('Error de conexión al cargar equipos. Verifica tu conexión a internet.');
      }
    }
  };

  const fetchPlayers = async () => {
    try {
      const result = await Promise.race([
        supabase
          .from('players')
          .select('*')
          .order('number'),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout al cargar jugadores')), 8000)
        )
      ]);
      
      if (result.error) {
        console.error('Error al cargar jugadores:', result.error);
      } else {
        setPlayers(result.data || []);
      }
    } catch (error) {
      console.error('Error al cargar jugadores:', error);
      if (error.message?.includes('Timeout') || error.message?.includes('timeout')) {
        alert('Error de conexión al cargar jugadores. Verifica tu conexión a internet.');
      }
    }
  };

  const fetchStaff = async () => {
    try {
      const result = await Promise.race([
        supabase
          .from('staff')
          .select('*')
          .order('name'),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout al cargar staff')), 8000)
        )
      ]);
      
      if (result.error) {
        console.error('Error al cargar staff:', result.error);
      } else {
        setStaff(result.data || []);
      }
    } catch (error) {
      console.error('Error al cargar staff:', error);
      if (error.message?.includes('Timeout') || error.message?.includes('timeout')) {
        alert('Error de conexión al cargar staff. Verifica tu conexión a internet.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Gestión de Equipos</h2>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-white/10 p-1 rounded-lg backdrop-blur-sm">
        {(['teams', 'players', 'staff'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-pink-500 text-white'
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            {tab === 'teams' ? 'Equipos' : tab === 'players' ? 'Jugadores' : 'Staff'}
          </button>
        ))}
      </div>

      {/* Filtro por equipo para jugadores y staff */}
      {(activeTab === 'players' || activeTab === 'staff') && (
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
          <label className="block text-sm font-medium text-white mb-2">
            Filtrar por equipo:
          </label>
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="w-full p-2 bg-white/10 border border-white/20 rounded-lg text-white"
          >
            <option value="">Todos los equipos</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id} className="bg-gray-800">
                {team.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Contenido según tab activo */}
      {activeTab === 'teams' && (
        <div className="space-y-4">
          <button
            onClick={() => setShowTeamForm(true)}
            className="bg-pink-500 hover:bg-pink-6 text-white px-4 py-2 rounded-lg font-medium"
          >
            Agregar Equipo
          </button>

          <div className="grid gap-4">
            {teams.map((team) => (
              <div key={team.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {(team.logo || team.photo) && (
                    <img
                      src={team.logo || team.photo}
                      alt={team.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-white">{team.name}</h3>
                    <p className="text-white/70 text-sm">{team.category}</p>
                    {team.is_own_team && (
                      <span className="inline-block bg-pink-500 text-white text-xs px-2 py-1 rounded-full mt-1">
                        Equipo Propio
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => editTeam(team)}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => deleteTeam(team.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'players' && (
        <div className="space-y-4">
          <button
            onClick={() => setShowPlayerForm(true)}
            className="bg-pink-500 hover:bg-pink-6 text-white px-4 py-2 rounded-lg font-medium"
          >
            Agregar Jugador
          </button>

          <div className="grid gap-4">
            {filteredPlayers.map((player) => {
              const team = teams.find(t => t.id === player.team_id);
              return (
                <div key={player.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {player.photo && (
                      <img
                        src={player.photo}
                        alt={player.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-white">#{player.number} {player.name}</h3>
                      <p className="text-white/70 text-sm">{player.position}</p>
                      {team && <p className="text-pink-400 text-sm">{team.name}</p>}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => editPlayer(player)}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => deletePlayer(player.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'staff' && (
        <div className="space-y-4">
          <button
            onClick={() => setShowStaffForm(true)}
            className="bg-pink-500 hover:bg-pink-6 text-white px-4 py-2 rounded-lg font-medium"
          >
            Agregar Staff
          </button>

          <div className="grid gap-4">
            {filteredStaff.map((staffMember) => {
              const team = teams.find(t => t.id === staffMember.team_id);
              return (
                <div key={staffMember.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {staffMember.photo && (
                      <img
                        src={staffMember.photo}
                        alt={staffMember.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-white">{staffMember.name}</h3>
                      <p className="text-white/70 text-sm">{staffMember.role}</p>
                      {team && <p className="text-pink-400 text-sm">{team.name}</p>}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => editStaff(staffMember)}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => deleteStaffMember(staffMember.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Formulario de Equipo */}
      {showTeamForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleTeamSubmit} className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white">
              {editingTeam ? 'Editar Equipo' : 'Agregar Equipo'}
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Nombre</label>
              <input
                type="text"
                value={teamForm.name}
                onChange={(e) => setTeamForm({...teamForm, name: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Categoría</label>
              <input
                type="text"
                value={teamForm.category}
                onChange={(e) => setTeamForm({...teamForm, category: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Logo del Equipo (para partidos y resultados)</label>
              <button
                type="button"
                onClick={() => openMediaSelector('logo')}
                className="w-full px-4 py-2 bg-white/10 text-gray-300 rounded-md hover:bg-white/20 border-2 border-dashed border-white/20 transition-colors"
              >
                <i className="ri-upload-line mr-2"></i>
                {teamForm.logo ? 'Cambiar Logo' : 'Seleccionar Logo'}
              </button>
              {teamForm.logo && (
                <div className="mt-3">
                  <p className="text-sm text-gray-300 mb-2">Vista previa del logo:</p>
                  <img
                    src={teamForm.logo}
                    alt="Logo Preview"
                    className="w-16 h-16 object-cover rounded-lg border border-white/20 mx-auto"
                  />
                  <button
                    type="button"
                    onClick={() => setTeamForm({ ...teamForm, logo: '' })}
                    className="mt-2 text-sm text-red-400 hover:text-red-300 block mx-auto"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Eliminar logo
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Imagen del Equipo (para plantilla)</label>
              <button
                type="button"
                onClick={() => openMediaSelector('team')}
                className="w-full px-4 py-2 bg-white/10 text-gray-300 rounded-md hover:bg-white/20 border-2 border-dashed border-white/20 transition-colors"
              >
                <i className="ri-upload-line mr-2"></i>
                {teamForm.photo ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
              </button>
              {teamForm.photo && (
                <div className="mt-3">
                  <p className="text-sm text-gray-300 mb-2">Vista previa de la imagen:</p>
                  <img
                    src={teamForm.photo}
                    alt="Team Photo Preview"
                    className="w-full h-32 object-cover rounded-lg border border-white/20"
                  />
                  <button
                    type="button"
                    onClick={() => setTeamForm({ ...teamForm, photo: '' })}
                    className="mt-2 text-sm text-red-400 hover:text-red-300"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Eliminar imagen
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_own_team"
                checked={teamForm.is_own_team}
                onChange={(e) => setTeamForm({...teamForm, is_own_team: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="is_own_team" className="text-sm text-white">
                Es nuestro equipo
              </label>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-pink-500 hover-bg-pink-600 text-white py-2 px-4 rounded font-medium"
              >
                {editingTeam ? 'Actualizar' : 'Agregar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowTeamForm(false);
                  setEditingTeam(null);
                  setTeamForm({ name: '', category: '', photo: '', logo: '', is_own_team: false });
                }}
                className="flex-1 bg-gray-600 hover-bg-gray-700 text-white py-2 px-4 rounded font-medium"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulario de Jugador */}
      {showPlayerForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <form onSubmit={handlePlayerSubmit} className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-xl font-bold text-white">
              {editingPlayer ? 'Editar Jugador' : 'Agregar Jugador'}
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Nombre</label>
              <input
                type="text"
                value={playerForm.name}
                onChange={(e) => setPlayerForm({...playerForm, name: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Número</label>
              <input
                type="number"
                value={playerForm.number}
                onChange={(e) => setPlayerForm({...playerForm, number: parseInt(e.target.value)})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Posición</label>
              <select
                value={playerForm.position}
                onChange={(e) => setPlayerForm({...playerForm, position: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              >
                <option value="">Seleccionar posición</option>
                <option value="Base" className="bg-gray-800">Base</option>
                <option value="Escolta" className="bg-gray-800">Escolta</option>
                <option value="Alero" className="bg-gray-800">Alero</option>
                <option value="Ala pívot" className="bg-gray-800">Ala pívot</option>
                <option value="Pívot" className="bg-gray-800">Pívot</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Equipo</label>
              <select
                value={playerForm.team_id}
                onChange={(e) => setPlayerForm({...playerForm, team_id: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              >
                <option value="">Seleccionar equipo</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id} className="bg-gray-800">
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Imagen del Jugador</label>
              <button
                type="button"
                onClick={() => openMediaSelector('player')}
                className="w-full px-4 py-2 bg-white/10 text-gray-300 rounded-md hover:bg-white/20 border-2 border-dashed border-white/20 transition-colors"
              >
                <i className="ri-upload-line mr-2"></i>
                {playerForm.photo ? 'Cambiar Imagen' : 'Seleccionar desde Medios'}
              </button>
              {playerForm.photo && (
                <div className="mt-3">
                  <p className="text-sm text-gray-300 mb-2">Vista previa:</p>
                  <img
                    src={playerForm.photo}
                    alt="Preview"
                    className="w-24 h-24 object-cover rounded-full border border-white/20 mx-auto"
                  />
                  <button
                    type="button"
                    onClick={() => setPlayerForm({ ...playerForm, photo: '' })}
                    className="mt-2 text-sm text-red-400 hover:text-red-300 block mx-auto"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Eliminar imagen
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-pink-500 hover-bg-pink-600 text-white py-2 px-4 rounded font-medium"
              >
                {editingPlayer ? 'Actualizar' : 'Agregar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPlayerForm(false);
                  setEditingPlayer(null);
                  setPlayerForm({ name: '', number: 0, position: '', photo: '', team_id: '' });
                }}
                className="flex-1 bg-gray-600 hover-bg-gray-700 text-white py-2 px-4 rounded font-medium"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulario de Staff */}
      {showStaffForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleStaffSubmit} className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-xl font-bold text-white">
              {editingStaff ? 'Editar Staff' : 'Agregar Staff'}
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Nombre</label>
              <input
                type="text"
                value={staffForm.name}
                onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Rol</label>
              <select
                value={staffForm.role}
                onChange={(e) => setStaffForm({...staffForm, role: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              >
                <option value="">Seleccionar rol</option>
                <option value="Entrenador" className="bg-gray-800">Entrenador</option>
                <option value="2º Entrenador" className="bg-gray-800">2º Entrenador</option>
                <option value="Delegado" className="bg-gray-800">Delegado</option>
                <option value="Fisio" className="bg-gray-800">Fisio</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Equipo</label>
              <select
                value={staffForm.team_id}
                onChange={(e) => setStaffForm({...staffForm, team_id: e.target.value})}
                className="w-full p-2 bg-white/10 border border-white/20 rounded text-white"
                required
              >
                <option value="">Seleccionar equipo</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id} className="bg-gray-800">
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1">Imagen del Staff</label>
              <button
                type="button"
                onClick={() => openMediaSelector('staff')}
                className="w-full px-4 py-2 bg-white/10 text-gray-300 rounded-md hover:bg-white/20 border-2 border-dashed border-white/20 transition-colors"
              >
                <i className="ri-upload-line mr-2"></i>
                {staffForm.photo ? 'Cambiar Imagen' : 'Seleccionar desde Medios'}
              </button>
              {staffForm.photo && (
                <div className="mt-3">
                  <p className="text-sm text-gray-300 mb-2">Vista previa:</p>
                  <img
                    src={staffForm.photo}
                    alt="Preview"
                    className="w-24 h-24 object-cover rounded-full border border-white/20 mx-auto"
                  />
                  <button
                    type="button"
                    onClick={() => setStaffForm({ ...staffForm, photo: '' })}
                    className="mt-2 text-sm text-red-400 hover:text-red-300 block mx-auto"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Eliminar imagen
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-pink-500 hover-bg-pink-600 text-white py-2 px-4 rounded font-medium"
              >
                {editingStaff ? 'Actualizar' : 'Agregar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowStaffForm(false);
                  setEditingStaff(null);
                  setStaffForm({ name: '', role: '', photo: '', team_id: '' });
                }}
                className="flex-1 bg-gray-600 hover-bg-gray-700 text-white py-2 px-4 rounded font-medium"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Editor de Imágenes */}
      {showImageEditor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-xl font-bold text-white mb-4">Ajustar Imagen</h3>
            
            <div className="space-y-4">
              {/* Vista previa de la imagen */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <img
                      src={imageEditorData.originalImage}
                      alt="Preview"
                      className="max-w-full max-h-80 object-contain rounded-lg"
                      style={{ maxHeight: '320px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Controles de ajuste */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Ajuste de imagen:
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const size = Math.min(img.width, img.height);
                          canvas.width = size;
                          canvas.height = size;
                          const x = (img.width - size) / 2;
                          const y = (img.height - size) / 2;
                          ctx?.drawImage(img, x, y, size, size, 0, 0, size, size);
                          setImageEditorData(prev => ({ ...prev, croppedImage: canvas.toDataURL() }));
                        };
                        img.src = imageEditorData.originalImage;
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Recorte Cuadrado
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          canvas.width = img.width;
                          canvas.height = img.height;
                          ctx?.drawImage(img, 0, 0);
                          setImageEditorData(prev => ({ ...prev, croppedImage: canvas.toDataURL() }));
                        };
                        img.src = imageEditorData.originalImage;
                      }}
                      className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                    >
                      Imagen Original
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Redimensionar:
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[200, 400, 600].map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          const canvas = document.createElement('canvas');
                          const ctx = canvas.getContext('2d');
                          const img = new Image();
                          img.onload = () => {
                            const aspectRatio = img.width / img.height;
                            let newWidth = size;
                            let newHeight = size;
                            
                            if (aspectRatio > 1) {
                              newHeight = size / aspectRatio;
                            } else {
                              newWidth = size * aspectRatio;
                            }
                            
                            canvas.width = newWidth;
                            canvas.height = newHeight;
                            ctx?.drawImage(img, 0, 0, newWidth, newHeight);
                            setImageEditorData(prev => ({ ...prev, croppedImage: canvas.toDataURL() }));
                          };
                          img.src = imageEditorData.originalImage;
                        }}
                        className="px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                      >
                        {size}px
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vista previa del resultado */}
              {imageEditorData.croppedImage !== imageEditorData.originalImage && (
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-300 mb-2">Vista previa del resultado:</p>
                  <div className="flex justify-center">
                    <img
                      src={imageEditorData.croppedImage}
                      alt="Cropped Preview"
                      className={`border border-white/20 ${
                        imageEditorData.type === 'team' 
                          ? 'w-24 h-24 rounded-lg object-cover' 
                          : 'w-20 h-20 rounded-full object-cover'
                      }`}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={handleImageSave}
                className="flex-1 bg-pink-500 hover:bg-pink-600 text-white py-2 px-4 rounded font-medium"
              >
                Usar esta imagen
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowImageEditor(false);
                  setImageEditorData({ originalImage: '', croppedImage: '', type: '' as any });
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
